import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useInvitePortalUserMutation } from "../../../../services/ProfileService";

interface InvitePortalUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  practiceId: string;
}

export function InvitePortalUserDialog({ open, onClose, onSuccess, practiceId }: InvitePortalUserDialogProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [inviteUser, { isLoading }] = useInvitePortalUserMutation();

  const handleSubmit = async () => {
    setError(null);

    if (!email || !firstName || !lastName) {
      setError("Email, first name, and last name are required.");
      return;
    }

    try {
      const result = await inviteUser({
        email,
        firstName,
        lastName,
        message: message || undefined,
        practiceId,
      }).unwrap();

      if (result.success) {
        onSuccess?.(`Invitation sent to ${email}.`);
        handleClose();
      } else if (result.conflict) {
        setError(`User already exists in practice "${result.conflict.existingPracticeName}" (${result.conflict.existingPracticeId}).`);
      } else {
        setError(result.errorMessage || "Failed to invite user.");
      }
    } catch (err: any) {
      if (err?.status === 409) {
        const conflict = err?.data;
        setError(`User already exists in practice "${conflict?.existingPracticeName || 'unknown'}".`);
      } else {
        setError(err?.data?.message || err?.data || "Failed to invite user.");
      }
    }
  };

  const handleClose = () => {
    setEmail("");
    setFirstName("");
    setLastName("");
    setMessage("");
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite Portal User</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
            />
          </Stack>
          <TextField
            label="Personal Message (optional)"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            minRows={3}
            maxRows={6}
            fullWidth
            inputProps={{ maxLength: 500 }}
            InputProps={{
              sx: {
                alignItems: 'flex-start',
              }
            }}
            helperText={`${message.length}/500`}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !email || !firstName || !lastName}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {isLoading ? "Sending..." : "Send Invitation"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
